import { useMemo, useState } from "react";
import { useSelectedTeam } from "@/context/teamContext";
import {
    type ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { TeamLeaderboard } from "@/api/tournamentAnalysis";
import { Loader } from "../common/loader";
import { getPointsBadge, formatNumber } from "@/lib/utils";
import { computeColumnRankings, getRankColor, getSortDirection } from "./tournamentTableUtils";
import { CompactStatsTable } from "@/components/general/Dashboard/Overview/CompactStatsTable";

interface TournamentRankingTableProps {
    data: TeamLeaderboard[];
    isLoading?: boolean;
    selectedCategory: StatCategory;
}

export type StatCategory = "overview" | "shooting" | "passing" | "puck" | "faceoffs" | "advanced" | "special";

const LOWER_IS_BETTER_COLUMNS = [
    "opponentStats.opponentGoals",
    "opponentStats.opponentShots",
    "opponentStats.opponentShotsOnGoal",
    "advancedStats.corsiAgainst",
    "advancedStats.fenwickAgainst",
    "penaltyKillStats.penaltyKillGoalsAgainst",
    "penaltyKillStats.penaltyKillCount",
    "puckStats.puckContestsLost",
];

const DEFAULT_SORT_BY_CATEGORY: Record<StatCategory, string> = {
    overview: "kpiInfo.avgKpi",
    shooting: "teamShotsStats.goals",
    passing: "passStats.successfulPasses",
    puck: "puckStats.puckContestsWon",
    faceoffs: "faceoffStats.faceoffWins",
    advanced: "advancedStats.corsiForPercentage",
    special: "powerplayStats.powerplayGoals",
};

function TeamRankingMobileCards({
    data,
    selectedCategory,
    selectedTeamId,
}: {
    data: TeamLeaderboard[];
    selectedCategory: StatCategory;
    selectedTeamId: number | undefined;
}) {
    const sorted = [...data].sort((a, b) => (b.kpiInfo.avgKpi ?? 0) - (a.kpiInfo.avgKpi ?? 0));

    const getSubScores = (team: TeamLeaderboard): { label: string; value: string | number }[] => {
        switch (selectedCategory) {
            case "overview":
                return [
                    { label: "Gls", value: team.teamShotsStats.goals },
                    { label: "GA", value: team.opponentStats.opponentGoals },
                    { label: "Shots", value: team.teamShotsStats.shots },
                    { label: "SOG", value: team.teamShotsStats.shotsOnGoal },
                ];
            case "shooting":
                return [
                    { label: "Gls", value: team.teamShotsStats.goals },
                    { label: "Shots", value: team.teamShotsStats.shots },
                    { label: "SOG", value: team.teamShotsStats.shotsOnGoal },
                    { label: "xG", value: team.teamShotsStats.expectedGoalsSum.toFixed(1) },
                ];
            case "passing":
                return [
                    { label: "Pass", value: team.passStats.successfulPasses },
                    { label: "Total", value: team.passStats.allPasses },
                    {
                        label: "Acc",
                        value: `${
                            team.passStats.allPasses > 0
                                ? ((team.passStats.successfulPasses / team.passStats.allPasses) * 100).toFixed(0)
                                : 0
                        }%`,
                    },
                ];
            case "puck":
                return [
                    { label: "Won", value: team.puckStats.puckContestsWon },
                    { label: "Lost", value: team.puckStats.puckContestsLost },
                    { label: "Total", value: team.puckStats.totalPuckContests },
                ];
            case "faceoffs":
                return [
                    { label: "Won", value: team.faceoffStats.faceoffWins },
                    { label: "Total", value: team.faceoffStats.faceoffCount },
                    {
                        label: "Win%",
                        value: `${
                            team.faceoffStats.faceoffCount > 0
                                ? ((team.faceoffStats.faceoffWins / team.faceoffStats.faceoffCount) * 100).toFixed(0)
                                : 0
                        }%`,
                    },
                ];
            case "advanced":
                return [
                    { label: "CF%", value: `${(team.advancedStats.corsiForPercentage * 100).toFixed(1)}%` },
                    { label: "FF%", value: `${(team.advancedStats.fenwickForPercentage * 100).toFixed(1)}%` },
                    { label: "PDO", value: team.advancedStats.pdo.toFixed(3) },
                ];
            case "special":
                return [
                    { label: "PP Gls", value: team.powerplayStats.powerplayGoals },
                    { label: "PP Opp", value: team.powerplayStats.powerplayCount },
                    { label: "PK GA", value: team.penaltyKillStats.penaltyKillGoalsAgainst },
                ];
            default:
                return [];
        }
    };

    return (
        <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
            {sorted.map((team, index) => {
                const rank = index + 1;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                const isSelected = team.teamIdentity.teamId === selectedTeamId;
                const avgKpi = team.kpiInfo.avgKpi ?? 0;
                const { bg, text, border } = getPointsBadge(avgKpi);
                const subScores = getSubScores(team);
                return (
                    <div
                        key={team.teamIdentity.teamId}
                        className={`flex items-center gap-3 pl-0 pr-3 py-3 ${rowBg} ${
                            isSelected ? "ring-1 ring-primary/40" : ""
                        }`}
                    >
                        <span
                            className={`font-headline font-bold text-sm w-9 text-center shrink-0 tabular-nums ${
                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                            }`}
                        >
                            #{rank.toString().padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-on-surface truncate mb-1.5">
                                {team.teamIdentity.teamName}
                            </div>
                            <div className="flex items-end gap-2">
                                {subScores.map(({ label, value }) => (
                                    <div key={label} className="flex flex-col items-center gap-0.5 w-9">
                                        <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider">
                                            {label}
                                        </span>
                                        <span className="w-full text-center text-xs tabular-nums text-on-surface font-medium leading-none">
                                            {value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="shrink-0 w-12 text-center">
                            {avgKpi > 0 ? (
                                <div
                                    className={`w-full text-center tabular-nums py-1 rounded font-bold text-sm border ${bg} ${text} ${border}`}
                                >
                                    {avgKpi}
                                </div>
                            ) : (
                                <span className="text-on-surface-variant text-sm">–</span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export function TournamentRankingTable({ data, isLoading = false, selectedCategory }: TournamentRankingTableProps) {
    const { teamId: selectedTeamId } = useSelectedTeam();
    const [sorting, setSorting] = useState<SortingState>([]);

    const columns = useMemo((): ColumnDef<TeamLeaderboard>[] => {
        const baseColumns: ColumnDef<TeamLeaderboard>[] = [
            {
                accessorKey: "teamIdentity.teamName",
                header: "Team",
                cell: ({ row }) => <div className="font-semibold">{row.original.teamIdentity.teamName}</div>,
            },
        ];

        const categoryColumns: Record<StatCategory, ColumnDef<TeamLeaderboard>[]> = {
            overview: [
                {
                    accessorKey: "kpiInfo.sumKpi",
                    header: "Total KPI",
                    cell: ({ row }) => formatNumber(row.original.kpiInfo.sumKpi),
                },
                {
                    accessorKey: "kpiInfo.avgKpi",
                    header: "Average KPI",
                    cell: ({ row }) => {
                        const kpi = row.original.kpiInfo.avgKpi;
                        if (!kpi) return "-";
                        const { bg, text, border } = getPointsBadge(kpi);
                        return (
                            <div
                                className={`inline-flex items-center justify-center w-16 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                            >
                                {kpi}
                            </div>
                        );
                    },
                },
                {
                    accessorKey: "teamShotsStats.goals",
                    header: "Goals",
                    cell: ({ row }) => row.original.teamShotsStats.goals,
                },
                {
                    accessorKey: "opponentStats.opponentGoals",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Goals Against</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Goals scored by opposing teams</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.opponentStats.opponentGoals,
                },
                {
                    accessorKey: "teamShotsStats.shots",
                    header: "Shots",
                    cell: ({ row }) => row.original.teamShotsStats.shots,
                },
                {
                    accessorKey: "teamShotsStats.shotsOnGoal",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">SOG</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Shots on Goal</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.teamShotsStats.shotsOnGoal,
                },
                {
                    id: "shotAccuracy",
                    header: "Shot Accuracy %",
                    accessorFn: (row) =>
                        row.teamShotsStats.shots > 0
                            ? ((row.teamShotsStats.shotsOnGoal / row.teamShotsStats.shots) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    id: "savePercentage",
                    header: "Save %",
                    accessorFn: (row) =>
                        row.opponentStats.opponentShotsOnGoal > 0
                            ? (
                                  ((row.opponentStats.opponentShotsOnGoal - row.opponentStats.opponentGoals) /
                                      row.opponentStats.opponentShotsOnGoal) *
                                  100
                              ).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
            shooting: [
                {
                    accessorKey: "teamShotsStats.shots",
                    header: "Total Shots",
                    cell: ({ row }) => row.original.teamShotsStats.shots,
                },
                {
                    accessorKey: "teamShotsStats.shotsOnGoal",
                    header: "Shots on Goal",
                    cell: ({ row }) => row.original.teamShotsStats.shotsOnGoal,
                },
                {
                    accessorKey: "teamShotsStats.shotsWithScreen",
                    header: "Screened Shots",
                    cell: ({ row }) => row.original.teamShotsStats.shotsWithScreen,
                },
                {
                    accessorKey: "teamShotsStats.goals",
                    header: "Goals",
                    cell: ({ row }) => row.original.teamShotsStats.goals,
                },
                {
                    id: "shootingPercentage",
                    header: "Conversion Rate",
                    accessorFn: (row) =>
                        row.teamShotsStats.shots > 0
                            ? ((row.teamShotsStats.goals / row.teamShotsStats.shots) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "teamShotsStats.expectedGoalsSum",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">xG</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Expected Goals</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.teamShotsStats.expectedGoalsSum.toFixed(1),
                },
                {
                    id: "xGDiff",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">xG Diff</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Expected Goals Difference (Goals - Expected Goals)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    accessorFn: (row) => (row.teamShotsStats.goals - row.teamShotsStats.expectedGoalsSum).toFixed(1),
                    cell: ({ getValue }) => {
                        const value = parseFloat(getValue() as string);
                        return (
                            <span
                                style={{
                                    color:
                                        value > 0 ? "var(--color-green)" : value < 0 ? "var(--color-red)" : undefined,
                                }}
                            >
                                {value > 0 ? "+" : ""}
                                {getValue() as string}
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "opponentStats.opponentShots",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Opp Shots</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Opponent Shots</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.opponentStats.opponentShots,
                },
                {
                    accessorKey: "opponentStats.opponentShotsOnGoal",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Opp SOG</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Opponent Shots on Goal</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.opponentStats.opponentShotsOnGoal,
                },
                {
                    accessorKey: "opponentStats.opponentGoals",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Opp Goals</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Opponent Goals</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.opponentStats.opponentGoals,
                },
            ],
            passing: [
                {
                    accessorKey: "passStats.successfulPasses",
                    header: "Successful Passes",
                    cell: ({ row }) => row.original.passStats.successfulPasses,
                },
                {
                    accessorKey: "passStats.allPasses",
                    header: "Total Passes",
                    cell: ({ row }) => row.original.passStats.allPasses,
                },
                {
                    id: "passAccuracy",
                    header: "Pass Accuracy %",
                    accessorFn: (row) =>
                        row.passStats.allPasses > 0
                            ? ((row.passStats.successfulPasses / row.passStats.allPasses) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "passStats.totalPassDistance",
                    header: "Total Pass Distance",
                    cell: ({ row }) => `${(row.original.passStats.totalPassDistance / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "passStats.forwardPassDistance",
                    header: "Forward Pass Distance",
                    cell: ({ row }) => `${(row.original.passStats.forwardPassDistance / 1000).toFixed(1)} km`,
                },
                {
                    id: "avgPassDistance",
                    header: "Avg Pass Distance",
                    accessorFn: (row) =>
                        row.passStats.successfulPasses > 0
                            ? (row.passStats.totalPassDistance / row.passStats.successfulPasses).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()} m`,
                },
            ],
            puck: [
                {
                    accessorKey: "puckStats.puckContestsWon",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Contests Won</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Puck Contests Won</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.puckStats.puckContestsWon,
                },
                {
                    accessorKey: "puckStats.puckContestsLost",
                    header: "Contests Lost",
                    cell: ({ row }) => row.original.puckStats.puckContestsLost,
                },
                {
                    accessorKey: "puckStats.totalPuckContests",
                    header: "Total Contests",
                    cell: ({ row }) => row.original.puckStats.totalPuckContests,
                },
                {
                    id: "puckContestWinRate",
                    header: "Contest Win %",
                    accessorFn: (row) =>
                        row.puckStats.totalPuckContests > 0
                            ? ((row.puckStats.puckContestsWon / row.puckStats.totalPuckContests) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "puckStats.puckControlDuration",
                    header: "Puck Control Time",
                    cell: ({ row }) => {
                        const seconds = row.original.puckStats.puckControlDuration;
                        const minutes = Math.floor(seconds / 60);
                        return `${minutes} min`;
                    },
                },
                {
                    accessorKey: "distanceStats.distanceTravelled",
                    header: "Distance Travelled",
                    cell: ({ row }) => `${(row.original.distanceStats.distanceTravelled / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "distanceStats.distanceTravelledWithPuck",
                    header: "Distance w/ Puck",
                    cell: ({ row }) => `${(row.original.distanceStats.distanceTravelledWithPuck / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "distanceStats.forwardDistanceTravelledWithPuck",
                    header: "Forward Distance w/ Puck",
                    cell: ({ row }) =>
                        `${(row.original.distanceStats.forwardDistanceTravelledWithPuck / 1000).toFixed(1)} km`,
                },
            ],
            faceoffs: [
                {
                    accessorKey: "faceoffStats.faceoffWins",
                    header: "Faceoff Wins",
                    cell: ({ row }) => row.original.faceoffStats.faceoffWins,
                },
                {
                    accessorKey: "faceoffStats.faceoffCount",
                    header: "Total Faceoffs",
                    cell: ({ row }) => row.original.faceoffStats.faceoffCount,
                },
                {
                    id: "faceoffWinPercentage",
                    header: "Faceoff Win %",
                    accessorFn: (row) =>
                        row.faceoffStats.faceoffCount > 0
                            ? ((row.faceoffStats.faceoffWins / row.faceoffStats.faceoffCount) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
            advanced: [
                {
                    accessorKey: "advancedStats.corsiFor",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Corsi For</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>All shot attempts for (shots + blocked shots + missed shots)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedStats.corsiFor,
                },
                {
                    accessorKey: "advancedStats.corsiAgainst",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Corsi Against</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>All shot attempts against (opponent shots + blocked + missed)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedStats.corsiAgainst,
                },
                {
                    accessorKey: "advancedStats.corsiForPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Corsi For %</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Percentage of all shot attempts taken by the team</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => `${(row.original.advancedStats.corsiForPercentage * 100).toFixed(0)}%`,
                },
                {
                    accessorKey: "advancedStats.fenwickFor",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Fenwick For</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Unblocked shot attempts for (shots + missed shots)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedStats.fenwickFor,
                },
                {
                    accessorKey: "advancedStats.fenwickAgainst",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Fenwick Against</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Unblocked shot attempts against (opponent shots + missed)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedStats.fenwickAgainst,
                },
                {
                    accessorKey: "advancedStats.fenwickForPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Fenwick For %</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Percentage of unblocked shot attempts taken by the team</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => `${(row.original.advancedStats.fenwickForPercentage * 100).toFixed(0)}%`,
                },
                {
                    accessorKey: "advancedStats.pdo",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PDO</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Sum of shooting percentage and save percentage (luck indicator)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedStats.pdo.toFixed(3),
                },
            ],
            special: [
                {
                    accessorKey: "powerplayStats.powerplayGoals",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PP Goals</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Power Play Goals</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.powerplayStats.powerplayGoals,
                },
                {
                    accessorKey: "powerplayStats.powerplayCount",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PP Opportunities</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Power Play Opportunities</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.powerplayStats.powerplayCount,
                },
                {
                    id: "powerplayPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PP %</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Power Play Success Percentage</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    accessorFn: (row) =>
                        row.powerplayStats.powerplayCount > 0
                            ? ((row.powerplayStats.powerplayGoals / row.powerplayStats.powerplayCount) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "penaltyKillStats.penaltyKillGoalsAgainst",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PK Goals Against</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Penalty Kill Goals Against</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.penaltyKillStats.penaltyKillGoalsAgainst,
                },
                {
                    accessorKey: "penaltyKillStats.penaltyKillCount",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PK Situations</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Penalty Kill Situations</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.penaltyKillStats.penaltyKillCount,
                },
                {
                    id: "penaltyKillPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PK %</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Penalty Kill Success Percentage</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    accessorFn: (row) =>
                        row.penaltyKillStats.penaltyKillCount > 0
                            ? (
                                  ((row.penaltyKillStats.penaltyKillCount -
                                      row.penaltyKillStats.penaltyKillGoalsAgainst) /
                                      row.penaltyKillStats.penaltyKillCount) *
                                  100
                              ).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
        };

        return [...baseColumns, ...categoryColumns[selectedCategory]];
    }, [selectedCategory]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    });

    const columnRankings = useMemo(
        () => computeColumnRankings(data, columns.slice(1), LOWER_IS_BETTER_COLUMNS),
        [data, columns]
    );

    if (isLoading) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <Loader message="Loading tournament ranking..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <span className="text-center p-4 text-on-surface-variant">No ranking data available</span>
            </div>
        );
    }

    return (
        <div className="w-full h-full min-h-0 flex flex-col">
            <div className="md:hidden overflow-y-auto h-full p-3">
                <TeamRankingMobileCards
                    data={data}
                    selectedCategory={selectedCategory}
                    selectedTeamId={selectedTeamId}
                />
            </div>
            <div className="hidden md:block xl:hidden w-full overflow-auto h-full min-h-0">
                <CompactStatsTable<TeamLeaderboard>
                    columns={columns}
                    data={data}
                    defaultSortId={DEFAULT_SORT_BY_CATEGORY[selectedCategory]}
                />
            </div>
            <div className="hidden xl:block w-full overflow-auto h-full min-h-0 bg-surface-container-lowest">
                <Table className="w-full min-w-max">
                    <TableHeader>
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                                {hg.headers.map((header, idx) => {
                                    const sortDir = getSortDirection(header);
                                    return (
                                        <TableHead
                                            key={header.id}
                                            onClick={
                                                header.column.getCanSort()
                                                    ? () =>
                                                          header.column.toggleSorting(
                                                              header.column.getIsSorted() === "asc"
                                                          )
                                                    : undefined
                                            }
                                            className={`
                                                px-6 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest
                                                sticky z-20 select-none transition-colors whitespace-nowrap
                                                ${header.column.getCanSort() ? "cursor-pointer" : ""}
                                                ${idx === 0 ? "z-30" : ""}
                                                ${sortDir ? "data-table-header-sorted" : ""}
                                                data-table-header
                                            `}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                left: idx === 0 ? "0px" : undefined,
                                            }}
                                        >
                                            {!header.isPlaceholder && (
                                                <div className="flex gap-1.5 items-center">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <span>
                                                            {sortDir === "asc" ? (
                                                                <ArrowUp
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-red)" }}
                                                                />
                                                            ) : sortDir === "desc" ? (
                                                                <ArrowDown
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-green)" }}
                                                                />
                                                            ) : (
                                                                <ArrowUpDown className="h-3.5 w-3.5 text-on-surface-variant/30" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            const isSelectedTeam = row.original.teamIdentity.teamId === selectedTeamId;
                            const baseBackground = isSelectedTeam
                                ? "color-mix(in srgb, var(--color-primary-token) 4%, transparent)"
                                : "";
                            return (
                                <TableRow
                                    key={row.id}
                                    className="data-table-row transition-colors"
                                    style={{
                                        background: baseBackground,
                                    }}
                                >
                                    {row.getVisibleCells().map((cell, idx) => {
                                        if (idx === 0) {
                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    className="data-table-label-cell sticky z-10 font-bold px-6 py-3 text-sm text-primary whitespace-nowrap"
                                                    style={{
                                                        left: "0px",
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            );
                                        }

                                        const value = cell.getValue();
                                        const columnDef = cell.column.columnDef as {
                                            accessorKey?: string;
                                            id?: string;
                                        };
                                        const columnId = (columnDef.accessorKey as string) || cell.column.id;
                                        const valueColor = getRankColor(value, columnId, columnRankings);

                                        return (
                                            <TableCell
                                                key={cell.id}
                                                className="px-6 py-2 text-[14px] text-on-surface whitespace-nowrap"
                                                style={{
                                                    color: valueColor,
                                                }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
